/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

"use client";

/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
*/

import React, { createContext, useContext, useMemo, useCallback } from "react";

import { getLocalizedPath, shouldHaveLocalePrefix, normalizePath } from "../../utils/locale-path.utils";

import type { LocalePrefixMode } from "../../types/locale.types";

/**
 * Context value for locale prefix configuration
 */
export interface LocalePrefixContextValue {
	/** The locale prefix mode ("always" | "as-needed" | "never") */
	localePrefix: LocalePrefixMode;
	/** The default locale for the site */
	defaultLocale: string;
	/**
	 * Generates a locale-aware path based on the prefix mode
	 * @param path - The path without locale prefix
	 * @param locale - The target locale
	 */
	getPath: (path: string, locale: string) => string;
	/**
	 * Checks if a given locale should have a URL prefix
	 * @param locale - The locale to check
	 */
	shouldPrefix: (locale: string) => boolean;
}

const LocalePrefixContext = createContext<LocalePrefixContextValue | null>(null);

/**
 * Fallback context value for sites not using LocalePrefixProvider.
 * Extracted as a constant to prevent creating new objects on every hook call.
 *
 * @remarks
 * - Uses "en-US" as default locale (common across Epic properties)
 * - Uses "always" prefix mode (backwards compatible behavior)
 * - Sites using "as-needed" or "never" modes SHOULD wrap with LocalePrefixProvider
 */
const FALLBACK_CONTEXT: LocalePrefixContextValue = {
	localePrefix: "always",
	defaultLocale: "en-US",
	getPath: (path: string, locale: string) => `/${locale}${normalizePath(path)}`,
	shouldPrefix: () => true,
};

/**
 * Props for LocalePrefixProvider
 */
export interface LocalePrefixProviderProps {
	children: React.ReactNode;
	/** The locale prefix mode. Defaults to "always" for backwards compatibility */
	localePrefix?: LocalePrefixMode;
	/** The default locale for the site (e.g., "en-US") */
	defaultLocale: string;
}

/**
 * Provider component for locale prefix configuration
 *
 * Wrap your app with this provider to enable locale-aware path generation
 * that respects the prefix mode configuration.
 *
 * @example
 * // In your Localization.tsx
 * import { LocalePrefixProvider } from "@cosmos/next-common";
 *
 * export const Localization = ({ children, locale }) => (
 *   <LocalePrefixProvider localePrefix="as-needed" defaultLocale="en-US">
 *     <I18NProvider locale={locale} ...>
 *       {children}
 *     </I18NProvider>
 *   </LocalePrefixProvider>
 * );
 */
export function LocalePrefixProvider({
	children,
	localePrefix = "always",
	defaultLocale,
}: LocalePrefixProviderProps): React.JSX.Element {
	const getPath = useCallback(
		(path: string, locale: string) => getLocalizedPath(path, locale, defaultLocale, localePrefix),
		[localePrefix, defaultLocale],
	);

	const shouldPrefix = useCallback(
		(locale: string) => shouldHaveLocalePrefix(locale, defaultLocale, localePrefix),
		[localePrefix, defaultLocale],
	);

	const value = useMemo<LocalePrefixContextValue>(
		() => ({
			localePrefix,
			defaultLocale,
			getPath,
			shouldPrefix,
		}),
		[localePrefix, defaultLocale, getPath, shouldPrefix],
	);

	return <LocalePrefixContext.Provider value={value}>{children}</LocalePrefixContext.Provider>;
}

/**
 * Hook to access locale prefix configuration
 *
 * Returns a fallback value if used outside of LocalePrefixProvider,
 * ensuring backwards compatibility with sites that haven't adopted the provider.
 *
 * @example
 * const { getPath, shouldPrefix } = useLocalePrefix();
 *
 * // Generate locale-aware paths
 * const aboutPath = getPath("/about", "de"); // "/de/about" or "/about" based on config
 *
 * // Check if locale needs prefix
 * if (shouldPrefix(locale)) {
 *   href = `/${locale}${href}`;
 * }
 */
export function useLocalePrefix(): LocalePrefixContextValue {
	const context = useContext(LocalePrefixContext);
	// Return constant fallback for sites not using the provider (backwards compatibility)
	return context ?? FALLBACK_CONTEXT;
}
